import React, {useRef, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {useProjects} from '../../../ProjectsContext';
import ImageManager from '../ImageManager';
import * as style from '../adminStyles';
import CategorySelector from "../../kategoriemanager/CategorySelector";
import ProjectFacts from "./ProjectFacts";

const API_BASE = '/api';

const emptyProject = {
    title: '',
    description: '',
    images: [''],
    position: [0, 0, -3],
    categories: [],
    facts: []
};

export default function ProjectForm() {
    const {id} = useParams();
    const {projects} = useProjects();
    const existing = id ? projects.find((p) => p.id === Number(id)) : null;

    if (id && !existing) {
        return (
            <div style={style.page}>
                <div style={style.container}>
                    <p>Projekt nicht gefunden.</p>
                    <Link to="/intern" style={style.button}>Zurück zur Liste</Link>
                </div>
            </div>
        );
    }

    return <FormFields initial={existing ?? emptyProject} isNew={!existing}/>;
}

function FormFields({initial, isNew}) {
    const navigate = useNavigate();
    const {saveProject, nextId} = useProjects();
    const fileInputRef = useRef(null);
    const pendingImageIndex = useRef(null);

    const [title, setTitle] = useState(initial.title);
    const [description, setDescription] = useState(initial.description);
    const [images, setImages] = useState(initial.images?.length ? initial.images : ['']);
    const [position, setPosition] = useState(initial.position.map(String));
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [uploadingIndex, setUploadingIndex] = useState(null);
    const [showImageManager, setShowImageManager] = useState(false);
    const [categories, setCategories] = useState(initial.categories ?? []);
    const [facts, setFacts] = useState(initial.facts ?? []);

    const setImage = (i, value) => setImages((list) => list.map((img, idx) => (idx === i ? value : img)));
    const removeImage = (i) => setImages((list) => (list.length > 1 ? list.filter((_, idx) => idx !== i) : ['']));
    const setPos = (i, value) => setPosition((p) => p.map((v, idx) => (idx === i ? value : v)));

    const triggerUpload = (index) => {
        pendingImageIndex.current = index;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        const index = pendingImageIndex.current;
        setUploadingIndex(index);
        setError('');

        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await fetch(`${API_BASE}/upload.php`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.ok) throw new Error(data.error || 'Upload fehlgeschlagen.');
            setImage(index, data.url);
        } catch (err) {
            setError(err.message || 'Upload fehlgeschlagen.');
        } finally {
            setUploadingIndex(null);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        const cleanImages = images.map((u) => u.trim()).filter(Boolean);
        const numbers = position.map((v) => Number(v));

        if (!title.trim()) return setError('Bitte einen Titel angeben.');
        if (cleanImages.length === 0) return setError('Bitte mindestens ein Bild angeben.');
        if (numbers.some((n) => Number.isNaN(n))) return setError('Position muss aus Zahlen bestehen.');

        setSaving(true);
        setError('');
        try {
            await saveProject({
                id: isNew ? nextId() : initial.id,
                title: title.trim(),
                facts: facts
                    .map(fact => ({
                        label: fact.label.trim(),
                        value: fact.value.trim()
                    }))
                    .filter(fact => fact.label && fact.value),
                description: description.trim(),
                images: cleanImages,
                position: numbers,
                categories: categories
            });
            navigate('/intern');
        } catch (err) {
            setError(err.message || 'Speichern fehlgeschlagen.');
            setSaving(false);
        }
    };

    return (
        <div style={style.page}>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{display: 'none'}}
                onChange={handleFileChange}
            />

            <form onSubmit={handleSave} style={style.container}>
                <div style={style.headerRow}>
                    <h1 style={style.h1}>{isNew ? 'Neues Projekt' : 'Projekt bearbeiten'}</h1>
                </div>

                <label style={style.label}>Titel</label>
                <input
                    style={style.input}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />

                <ProjectFacts value={facts} onChange={setFacts}/>

                <label style={style.label}>Beschreibung</label>
                <textarea
                    style={{...style.input, minHeight: '140px', resize: 'vertical'}}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />

                <label style={style.label}>Bilder</label>
                {images.map((url, i) => (
                    <div key={i} style={{display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center'}}>
                        {url && (
                            <img
                                src={url}
                                alt=""
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    background: '#f5f5f5',
                                    flexShrink: 0
                                }}
                            />
                        )}
                        <input
                            style={style.input}
                            placeholder="https://… oder Datei hochladen"
                            value={url}
                            onChange={(e) => setImage(i, e.target.value)}
                        />
                        <button
                            type="button"
                            style={{...style.button, whiteSpace: 'nowrap'}}
                            onClick={() => triggerUpload(i)}
                            disabled={uploadingIndex === i}
                        >
                            {uploadingIndex === i ? 'Lädt…' : 'Hochladen'}
                        </button>
                        <button type="button" style={style.button} onClick={() => removeImage(i)}
                                aria-label="Bild entfernen">
                            ×
                        </button>
                    </div>
                ))}

                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                    <button type="button" style={style.button} onClick={() => setImages((l) => [...l, ''])}>
                        + Bild hinzufügen
                    </button>
                    <button type="button" style={style.button} onClick={() => setShowImageManager(true)}>
                        Aus hochgeladenen Bildern wählen / verwalten
                    </button>
                </div>
                <p style={style.hint}>Das erste Bild ist zugleich der Stern im Universum.</p>

                <label style={style.label}>Position im Raum (x, y, z)</label>
                <div style={{display: 'flex', gap: '8px'}}>
                    {['x', 'y', 'z'].map((axis, i) => (
                        <input
                            key={axis}
                            style={style.input}
                            type="number"
                            step="0.5"
                            placeholder={axis}
                            value={position[i]}
                            onChange={(e) => setPos(i, e.target.value)}
                        />
                    ))}
                </div>
                <div style={{marginTop: '2%'}}>
                    <CategorySelector value={categories} onChange={setCategories} />
                </div>

                {error && <p style={{...style.hint, color: '#c0392b', marginTop: '16px'}}>{error}</p>}

                <div style={{display: 'flex', gap: '8px', marginTop: '32px'}}>
                    <Link to="/intern" style={style.button}>Abbrechen</Link>
                    <button type="submit" style={{...style.primaryButton, opacity: saving ? 0.6 : 1}} disabled={saving}>
                        {saving ? 'Speichert…' : 'Speichern'}
                    </button>
                </div>
            </form>

            {showImageManager && (
                <ImageManager
                    onClose={() => setShowImageManager(false)}
                    onPick={(url) => {
                        setImages((list) => (list[list.length - 1] === '' ? [...list.slice(0, -1), url] : [...list, url]));
                        setShowImageManager(false);
                    }}
                />
            )}
        </div>
    );
}
